import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, mergeProps as _mergeProps, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-1" };
const _hoisted_2 = { class: "flex items-center gap-x-2 text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)" };
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "flex flex-col gap-y-5" };
const _hoisted_5 = { class: "flex flex-col gap-y-1" };
const _hoisted_6 = { class: "text-sm font-medium text-(--text-text-black-default) dark:text-(--text-text-white-default) mb-1" };
const _hoisted_7 = { class: "flex gap-x-4" };
const _hoisted_8 = {
    key: 0,
    class: "flex flex-col gap-y-1"
};
const _hoisted_9 = { class: "text-sm font-medium text-(--text-text-black-default) dark:text-(--text-text-white-default)" };
const _hoisted_10 = {
    class: "flex items-center gap-x-2 rounded-xl border border-(--border-border-base-soft) bg-(--background-background-white-secondary-soft) p-3 text-sm text-(--text-text-black-default)",
    "data-testid": "inherited-address"
};
const _hoisted_11 = { class: "truncate" };
const _hoisted_12 = { class: "flex items-center gap-x-1 text-xs text-(--text-text-body)" };
const _hoisted_13 = {
    key: 1,
    class: "text-sm text-(--text-text-error)",
    role: "alert"
};
import { AutoComplete, Button, Modal, RadioButton, SelectInput, ToggleInput, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { computed, reactive, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAppToast } from "@/shared/composables/useAppToast";
import { applyParsedAddress, parseAddressQuery, seedAddressDetails, } from "@/modules/organisation/domain/address-utils";
import { useAddressFormatter } from "@/shared/composables/useAddressFormatter";
import { useWorkspaceCountry } from "@/shared/composables/useWorkspaceCountry";
import { defaultAddressCountry, requiredAddressFieldsFilled, toAddressLayout, } from "@/shared/utils/addressFieldConfig";
import { useAddressSearch, } from "@/modules/organisation/composables/useAddressSearch";
import AddressDetailsModal from "@/modules/organisation/presentation/components/AddressDetailsModal.vue";
import CreateOrganisationModal from "@/modules/organisation/presentation/components/CreateOrganisationModal.vue";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { logger } from "@/infrastructure/services/LoggerService";
import { createContactOrgAddressLink, getOrganisationPrimaryAddress, searchOrganisationsForPicker, } from "../../infrastructure/api/contactAddressApi";
import { autoCreateOrganisationRelation } from "../../infrastructure/api/contactRelationsApi";
import { addContactAddress, } from "../../infrastructure/adapters/contactService";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ContactAddressFormModal',
    props: {
        modelValue: { type: Boolean },
        contactId: {},
        existingAddresses: {},
        defaultContext: {}
    },
    emits: ["update:modelValue", "created"],
    setup(__props, { emit: __emit }) {
        const PERSONAL_ADDRESS_TYPE_OPTIONS = ["Home", "Work", "Billing", "Shipping", "Other"];
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const addressFormatter = useAddressFormatter();
        const workspaceCountry = useWorkspaceCountry();
        const defaultCountry = computed(() => defaultAddressCountry(workspaceCountry.value));
        const { toastError } = useAppToast();
        const typeOptions = computed(() => PERSONAL_ADDRESS_TYPE_OPTIONS.map((type) => ({
            value: type,
            label: t(`contact.detail.addresses.types.${type}`),
        })));
        // errorCode → i18n key (R70). The Path D conflict (409) is also pre-checked client
        // side; this maps the backend code in case the link is forced past the FE guard.
        const ERROR_OVERRIDES = {
            "Contact.OrgAddressLink.AlreadyExistsForOrganisation": "contact.detail.addresses.formModal.errors.alreadyAnchored",
        };
        // ---------------------------------------------------------------------------
        // Local form state
        // ---------------------------------------------------------------------------
        const context = ref(props.defaultContext ?? "Professional");
        const isFirstAddress = computed(() => props.existingAddresses.length === 0);
        const isPrimary = ref(false);
        const selectedType = ref("Home");
        const hintId = useId();
        const isSaving = ref(false);
        const organisationQuery = ref("");
        // Latest text typed in the organisation picker, carried over to pre-fill the
        // nested create modal's legal-name field (OP-6103). Kept separate from
        // `organisationQuery` (the AutoComplete v-model, which holds the selected id and
        // is cleared on every keystroke) — the typed term only arrives via `@search`.
        const organisationSearchTerm = ref("");
        const addressQuery = ref("");
        const form = reactive({
            organisationId: "",
            organisationName: "",
            line1: "",
            line2: "",
            line3: "",
            streetName: "",
            houseNumber: "",
            houseNumberSuffix: "",
            postalCode: "",
            city: "",
            region: "",
            country: "",
        });
        // Inherited (professional) primary address, resolved on organisation selection.
        const inheritedAddress = ref(null);
        const inheritResolving = ref(false);
        function resetForm() {
            context.value = props.defaultContext ?? "Professional";
            isPrimary.value = props.existingAddresses.length === 0;
            selectedType.value = "Home";
            isSaving.value = false;
            organisationQuery.value = "";
            organisationSearchTerm.value = "";
            addressQuery.value = "";
            form.organisationId = "";
            form.organisationName = "";
            form.line1 = "";
            form.line2 = "";
            form.line3 = "";
            form.streetName = "";
            form.houseNumber = "";
            form.houseNumberSuffix = "";
            form.postalCode = "";
            form.city = "";
            form.region = "";
            form.country = "";
            organisationSuggestions.value = [];
            addressSuggestions.value = [];
            seededAddressLabel.value = "";
            inheritedAddress.value = null;
            inheritResolving.value = false;
            showAddressDetailsModal.value = false;
            showCreateOrg.value = false;
            addressDetails.line1 = "";
            addressDetails.line2 = "";
            addressDetails.line3 = "";
            addressDetails.postalCode = "";
            addressDetails.city = "";
            addressDetails.country = defaultCountry.value;
        }
        const onContextChange = (next) => {
            context.value = next;
        };
        // ---------------------------------------------------------------------------
        // Organisation lookup
        // ---------------------------------------------------------------------------
        const organisationSuggestions = ref([]);
        const organisationLoading = ref(false);
        let organisationDebounce = null;
        const clearSelectedOrganisation = () => {
            form.organisationId = "";
            form.organisationName = "";
            inheritedAddress.value = null;
            inheritResolving.value = false;
        };
        // ─── Nested: Create new organisation (same pattern as relation/foyer modals) ──
        const showCreateOrg = ref(false);
        const createOrgMounted = ref(false);
        watch(showCreateOrg, (v) => {
            if (v)
                createOrgMounted.value = true;
        });
        function openCreateOrg() {
            showCreateOrg.value = true;
        }
        function onOrgCreated(newId, name) {
            showCreateOrg.value = false;
            const option = { value: newId, label: name || newId };
            organisationSuggestions.value = [option];
            onOrganisationSelect(option);
        }
        const onOrganisationSearch = (query) => {
            // Remember the latest term so the nested create modal can pre-fill it (OP-6103).
            organisationSearchTerm.value = query.trim();
            // If the user edits the picker text away from the selected org, clear the id.
            if (query !== form.organisationName) {
                clearSelectedOrganisation();
            }
            if (organisationDebounce)
                clearTimeout(organisationDebounce);
            if (query.trim().length < 2) {
                organisationSuggestions.value = [];
                return;
            }
            organisationDebounce = setTimeout(async () => {
                organisationLoading.value = true;
                try {
                    const results = await searchOrganisationsForPicker(query);
                    organisationSuggestions.value = results.map((o) => ({ value: o.id, label: o.name }));
                }
                catch {
                    toastError("contact.detail.addresses.formModal.errors.orgSearchFailed");
                    organisationSuggestions.value = [];
                }
                finally {
                    organisationLoading.value = false;
                }
            }, 300);
        };
        // Monotonic sequence guard: a newer resolution discards an in-flight stale one.
        let inheritResolveSeq = 0;
        const resolveInheritPrimary = async (orgId) => {
            inheritedAddress.value = null;
            inheritResolving.value = true;
            const seq = ++inheritResolveSeq;
            try {
                const primary = await getOrganisationPrimaryAddress(orgId);
                if (seq !== inheritResolveSeq)
                    return; // stale — a newer call took over
                inheritedAddress.value = primary;
            }
            catch {
                if (seq !== inheritResolveSeq)
                    return;
                toastError("contact.detail.addresses.formModal.errors.loadOrgAddressesFailed");
                inheritedAddress.value = null;
            }
            finally {
                if (seq === inheritResolveSeq)
                    inheritResolving.value = false;
            }
        };
        const onOrganisationSelect = (option) => {
            form.organisationId = option.value;
            form.organisationName = option.label;
            organisationQuery.value = option.label;
            // An existing org was picked — drop the captured term so a subsequent "create
            // new" (opened without retyping) doesn't pre-fill a stale name (OP-6103).
            organisationSearchTerm.value = "";
            // Skip the network resolution when Path D already blocks this organisation.
            if (!alreadyAnchored.value) {
                void resolveInheritPrimary(option.value);
            }
        };
        // Path D: a professional (linked) address is already anchored to the selected org.
        const alreadyAnchored = computed(() => !!form.organisationId &&
            props.existingAddresses.some((a) => a.source === "Linked" && a.organisationId === form.organisationId));
        const inheritedAddressText = computed(() => inheritedAddress.value ? formatOrganisationAddress(inheritedAddress.value) : "");
        function formatOrganisationAddress(a) {
            const cityLine = [a.postalCode, a.city].filter(Boolean).join(" ").trim();
            return [a.line1, a.line2, a.line3, cityLine, a.country]
                .map((part) => part?.trim())
                .filter(Boolean)
                .join(", ");
        }
        // ---------------------------------------------------------------------------
        // Address lookup (Personal)
        // ---------------------------------------------------------------------------
        const { suggestions: addressSuggestions, loading: addressLoading, search: _onAddressSearch, } = useAddressSearch(3); // 3-char floor: matches the BFF SearchAdresseQueryValidator (MinimumLength(3)) and the upstream BAN API, which both reject shorter queries (OP-6112)
        // Tracks the label of the last selected suggestion so onAddressSearch can
        // detect edits that diverge from it and clear the stale structured fields,
        // forcing buildPersonalPayload to re-parse the typed text.
        const seededAddressLabel = ref("");
        function onAddressSearch(query) {
            addressQuery.value = query;
            if (query !== seededAddressLabel.value) {
                // User edited away from the selection — stale structured fields must be
                // cleared so hasStructured doesn't suppress the re-parse in buildPersonalPayload.
                form.line1 = "";
                form.line2 = "";
                form.line3 = "";
                form.streetName = "";
                form.houseNumber = "";
                form.houseNumberSuffix = "";
                form.postalCode = "";
                form.city = "";
                form.region = "";
                form.country = "";
                seededAddressLabel.value = "";
            }
            _onAddressSearch(query);
        }
        const onAddressSelect = (option) => {
            addressQuery.value = option.label;
            seededAddressLabel.value = option.label;
            // A picked suggestion replaces the address wholesale — stale country-specific
            // fields must not survive under the new street.
            form.streetName = "";
            form.houseNumber = "";
            form.houseNumberSuffix = "";
            form.region = "";
            applyParsedAddress(option.label, form, workspaceCountry.value, option.meta?.country ?? (form.country || defaultCountry.value), addressFormatter.names);
            if (option.meta?.city)
                form.city = option.meta.city;
            if (option.meta?.postalCode)
                form.postalCode = option.meta.postalCode;
        };
        // ---------------------------------------------------------------------------
        // Address details sub-modal (Personal): shared AddressDetailsModal (same design
        // as organisation create). `addressDetails` is the modal's working copy.
        // ---------------------------------------------------------------------------
        const showAddressDetailsModal = ref(false);
        const detailsShowRequiredErrors = ref(false);
        const addressDetails = reactive({
            line1: "",
            line2: "",
            line3: "",
            streetName: "",
            houseNumber: "",
            houseNumberSuffix: "",
            postalCode: "",
            city: "",
            region: "",
            country: defaultCountry.value,
        });
        const openAddressDetailsModal = (showRequiredErrors = false) => {
            detailsShowRequiredErrors.value = showRequiredErrors;
            Object.assign(addressDetails, seedAddressDetails(form, parseAddressQuery(addressQuery.value, addressFormatter.names), defaultCountry.value, workspaceCountry.value));
            showAddressDetailsModal.value = true;
        };
        const saveAddressDetails = (details) => {
            form.line1 = details.line1;
            form.line2 = details.line2;
            form.line3 = details.line3;
            form.streetName = details.streetName;
            form.houseNumber = details.houseNumber;
            form.houseNumberSuffix = details.houseNumberSuffix;
            form.postalCode = details.postalCode;
            form.city = details.city;
            form.region = details.region;
            form.country = details.country;
            addressQuery.value = addressFormatter.format(form);
        };
        // ---------------------------------------------------------------------------
        // Derived validity
        // ---------------------------------------------------------------------------
        // Structured `form.line1` wins; otherwise parse the autocomplete string.
        const effectiveLine1 = computed(() => {
            if (form.line1.trim())
                return form.line1.trim();
            return parseAddressQuery(addressQuery.value, addressFormatter.names)?.line1?.trim() ?? "";
        });
        const hasResolvableAddress = computed(() => addressQuery.value.trim().length > 0 && effectiveLine1.value.length > 0);
        const canSave = computed(() => {
            if (context.value === "Personal")
                return hasResolvableAddress.value;
            // Professional (Path B1 — inherit only)
            if (!form.organisationId || alreadyAnchored.value)
                return false;
            return !inheritResolving.value && !!inheritedAddress.value;
        });
        // ---------------------------------------------------------------------------
        // Submit — persistence owned here so Save stays disabled until it completes.
        // ---------------------------------------------------------------------------
        // null when the address layout's required fields are missing after the parse.
        function buildPersonalPayload() {
            // Country defaults to France, so it can't gate the parse fallback — key off the
            // street/locality fields, which are only set once an address is resolved.
            const hasStructured = !!(form.line1 || form.streetName || form.houseNumber || form.postalCode || form.city);
            if (!hasStructured) {
                applyParsedAddress(addressQuery.value, form, workspaceCountry.value, form.country || defaultCountry.value, addressFormatter.names);
            }
            const layout = workspaceCountry.value;
            const saved = toAddressLayout(form, layout);
            // line1 alone is not enough in the NL layout: its house number is required too.
            if (!requiredAddressFieldsFilled(saved, layout))
                return null;
            return {
                type: selectedType.value,
                addressLine: saved.line1 || undefined,
                addressLine2: saved.line2 || undefined,
                addressLine3: saved.line3 || undefined,
                streetName: saved.streetName || undefined,
                houseNumber: saved.houseNumber || undefined,
                houseNumberSuffix: saved.houseNumberSuffix || undefined,
                postalCode: saved.postalCode || undefined,
                city: saved.city || undefined,
                region: saved.region || undefined,
                country: saved.country || defaultCountry.value || undefined,
                isPrimary: isPrimary.value,
            };
        }
        const handleSave = async () => {
            if (!canSave.value || isSaving.value)
                return;
            isSaving.value = true;
            try {
                if (context.value === "Personal") {
                    const payload = buildPersonalPayload();
                    if (!payload) {
                        openAddressDetailsModal(true);
                        return;
                    }
                    await addContactAddress(props.contactId, payload);
                }
                else {
                    // Professional, Path B1: link the org's primary address (resolved on selection).
                    await createContactOrgAddressLink(props.contactId, {
                        organisationId: form.organisationId,
                        addressId: inheritedAddress.value.id,
                        isPrimary: isPrimary.value,
                    });
                    // OP-5900: auto-create a contact↔organisation relation when a professional
                    // address is anchored to an organisation — mirrors ContactAddEmailModal / ContactAddPhoneModal.
                    // Best-effort: a failure must not surface to the user — the link was already persisted.
                    try {
                        await autoCreateOrganisationRelation(props.contactId, form.organisationId);
                    }
                    catch (err) {
                        logger.error(`Auto-relation creation failed after address link save for contact ${props.contactId}`, err);
                    }
                }
                emit("created");
                emit("update:modelValue", false);
            }
            catch (e) {
                const code = getApiErrorCode(e);
                const key = code && ERROR_OVERRIDES[code] ? ERROR_OVERRIDES[code] : "contact.detail.addresses.addError";
                toastError(key);
            }
            finally {
                isSaving.value = false;
            }
        };
        // Reset the local form every time the modal is (re)opened. Registered last so
        // the initial `immediate` run sees every ref above already initialised.
        watch(() => props.modelValue, (visible) => {
            if (visible)
                resetForm();
        }, { immediate: true });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    "model-value": __props.modelValue,
                    size: "sm",
                    "show-close-button": true,
                    "footer-position": "right",
                    "close-on-escape": !showAddressDetailsModal.value,
                    "aria-describedby": _unref(hintId),
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => (_ctx.$emit('update:modelValue', $event)))
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("h2", _hoisted_2, [
                                _cache[12] || (_cache[12] = _createElementVNode("span", {
                                    class: "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-(--border-border-base-soft) bg-white",
                                    "aria-hidden": "true"
                                }, [
                                    _createElementVNode("i", { class: "ri-map-pin-2-line text-xl text-(--text-text-black-default)" })
                                ], -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(_ctx.$t("contact.detail.addresses.formModal.titleAdd")), 1 /* TEXT */)
                            ]),
                            _createElementVNode("p", {
                                id: _unref(hintId),
                                class: "text-sm font-normal leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(_ctx.$t("contact.detail.addresses.formModal.requiredFieldsHint")), 9 /* TEXT, PROPS */, _hoisted_3)
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('contact.detail.addresses.formModal.cancel'),
                            onClick: _cache[8] || (_cache[8] = ($event) => (_ctx.$emit('update:modelValue', false)))
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            label: _ctx.$t('contact.detail.addresses.formModal.save'),
                            disabled: !canSave.value || isSaving.value,
                            onClick: handleSave
                        }, null, 8 /* PROPS */, ["label", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("fieldset", _hoisted_5, [
                                _createElementVNode("legend", _hoisted_6, [
                                    _createTextVNode(_toDisplayString(_ctx.$t("contact.detail.addresses.formModal.context.label")) + " ", 1 /* TEXT */),
                                    _cache[13] || (_cache[13] = _createElementVNode("span", { "aria-hidden": "true" }, "*", -1 /* CACHED */))
                                ]),
                                _createElementVNode("div", _hoisted_7, [
                                    _createVNode(_unref(RadioButton), {
                                        id: "contact-address-context-professional",
                                        name: "contact-address-context",
                                        "model-value": context.value,
                                        value: "Professional",
                                        label: _ctx.$t('contact.detail.addresses.formModal.context.professional'),
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (onContextChange('Professional')))
                                    }, null, 8 /* PROPS */, ["model-value", "label"]),
                                    _createVNode(_unref(RadioButton), {
                                        id: "contact-address-context-personal",
                                        name: "contact-address-context",
                                        "model-value": context.value,
                                        value: "Personal",
                                        label: _ctx.$t('contact.detail.addresses.formModal.context.personal'),
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (onContextChange('Personal')))
                                    }, null, 8 /* PROPS */, ["model-value", "label"])
                                ])
                            ]),
                            (context.value === 'Professional')
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: organisationQuery.value,
                                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((organisationQuery).value = $event)),
                                        label: _ctx.$t('contact.detail.addresses.formModal.organisation.label'),
                                        placeholder: _ctx.$t('contact.detail.addresses.formModal.organisation.placeholder'),
                                        required: true,
                                        options: organisationSuggestions.value,
                                        loading: organisationLoading.value,
                                        error: alreadyAnchored.value ? _ctx.$t('contact.detail.addresses.formModal.errors.alreadyAnchored') : undefined,
                                        "icon-left": "ri-building-line",
                                        "full-width": "",
                                        "create-label": _ctx.$t('common.createNewOrganisation'),
                                        onSearch: onOrganisationSearch,
                                        onSelect: onOrganisationSelect,
                                        onCreate: _cache[3] || (_cache[3] = ($event) => (openCreateOrg()))
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "error", "create-label"]),
                                    (!alreadyAnchored.value && form.organisationId)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                            _createElementVNode("span", _hoisted_9, [
                                                _createTextVNode(_toDisplayString(_ctx.$t("contact.detail.addresses.formModal.address.label")), 1 /* TEXT */),
                                                _cache[14] || (_cache[14] = _createElementVNode("span", { "aria-hidden": "true" }, "*", -1 /* CACHED */))
                                            ]),
                                            (inheritedAddressText.value)
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                    _createElementVNode("div", _hoisted_10, [
                                                        _cache[15] || (_cache[15] = _createElementVNode("i", {
                                                            class: "ri-map-pin-2-line shrink-0",
                                                            "aria-hidden": "true"
                                                        }, null, -1 /* CACHED */)),
                                                        _createElementVNode("span", _hoisted_11, _toDisplayString(inheritedAddressText.value), 1 /* TEXT */)
                                                    ]),
                                                    _createElementVNode("p", _hoisted_12, [
                                                        _cache[16] || (_cache[16] = _createElementVNode("i", {
                                                            class: "ri-information-line shrink-0",
                                                            "aria-hidden": "true"
                                                        }, null, -1 /* CACHED */)),
                                                        _createElementVNode("span", null, _toDisplayString(_ctx.$t("contact.detail.addresses.formModal.inheritHint")), 1 /* TEXT */)
                                                    ])
                                                ], 64 /* STABLE_FRAGMENT */))
                                                : (!inheritResolving.value)
                                                    ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(_ctx.$t("contact.detail.addresses.formModal.noOrgPrimary")), 1 /* TEXT */))
                                                    : _createCommentVNode("v-if", true)
                                        ]))
                                        : _createCommentVNode("v-if", true)
                                ], 64 /* STABLE_FRAGMENT */))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: addressQuery.value,
                                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((addressQuery).value = $event)),
                                        label: _ctx.$t('contact.detail.addresses.formModal.address.label'),
                                        placeholder: _ctx.$t('contact.detail.addresses.formModal.address.placeholder'),
                                        required: true,
                                        options: _unref(addressSuggestions),
                                        loading: _unref(addressLoading),
                                        "icon-left": "ri-map-pin-2-line",
                                        "show-add-more-details": "",
                                        "add-more-details-label": _ctx.$t('contact.detail.addresses.formModal.address.addDetails'),
                                        "keep-input-on-edit": "",
                                        "full-width": "",
                                        onSearch: onAddressSearch,
                                        onSelect: onAddressSelect,
                                        onAddMoreDetails: _cache[5] || (_cache[5] = ($event) => (openAddressDetailsModal()))
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "add-more-details-label"]),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: selectedType.value,
                                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((selectedType).value = $event)),
                                        label: _ctx.$t('contact.detail.addresses.formModal.type.label'),
                                        options: typeOptions.value,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "options"])
                                ], 64 /* STABLE_FRAGMENT */)),
                            _createVNode(_unref(ToggleInput), {
                                modelValue: isPrimary.value,
                                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((isPrimary).value = $event)),
                                type: "default",
                                label: _ctx.$t('contact.detail.addresses.formModal.setAsPrimary'),
                                disabled: isFirstAddress.value
                            }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["model-value", "close-on-escape", "aria-describedby"]),
                _createVNode(AddressDetailsModal, {
                    modelValue: showAddressDetailsModal.value,
                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event) => ((showAddressDetailsModal).value = $event)),
                    "initial-details": addressDetails,
                    "show-required-errors": detailsShowRequiredErrors.value,
                    onSave: saveAddressDetails
                }, null, 8 /* PROPS */, ["modelValue", "initial-details", "show-required-errors"]),
                (createOrgMounted.value)
                    ? (_openBlock(), _createBlock(CreateOrganisationModal, {
                        key: 0,
                        modelValue: showCreateOrg.value,
                        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((showCreateOrg).value = $event)),
                        "redirect-on-create": false,
                        "initial-name": organisationSearchTerm.value,
                        onCreated: onOrgCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-name"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
